import {
    RiAlarmLine,
    RiTimer2Line,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement, ReactNode, useEffect, useState,
} from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import prettyMilliseconds from "pretty-ms";
import ReactTimeago from "react-timeago";
import { TicketItem } from "../../../../contexts/ticket-state";
import { tryParseDate } from "../../../../date";
import Badge from "../../../theme/badge/Badge";
import Styles from "./Sla.module.scss";
import { DueDateType, TicketDueDate } from "../../../../__generated__/api";
import ExpandingSection from "../../../expanding-section/ExpandingSection";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";

export const Titles = {
    [DueDateType.SlaFirstResponse]: "1st response",
    [DueDateType.SlaTimeToUpdate]: "Next update",
    [DueDateType.SlaRequesterWaitTime]: "Requester Wait",

};

function useIsInViolation({
    ticketDueDate,
    isDueDateHistoryExpanded,
}: {
    ticketDueDate: TicketDueDate | undefined;
    isDueDateHistoryExpanded: boolean | undefined
}) {
    const completedDate = tryParseDate(ticketDueDate?.completedDate);
    const now = Date.now();

    const dueDate = tryParseDate(ticketDueDate?.dueDate) ?? new Date();
    const [isInViolation, setIsInViolation] = useState(dueDate.getTime() < (completedDate?.getTime() ?? now));

    const shouldDisplay = !ticketDueDate?.completedDate || isDueDateHistoryExpanded;
    useEffect(() => {
        if (ticketDueDate && !completedDate) {
            const timeLeft = dueDate.getTime() - Date.now();
            if (timeLeft > 0) {
                const timeout = setTimeout(() => {
                    setIsInViolation(!ticketDueDate.completedDate && dueDate.getTime() < Date.now());
                }, timeLeft);

                return () => {
                    clearTimeout(timeout);
                };
            }
        }
        return undefined;
    }, [!completedDate && shouldDisplay]);

    return isInViolation;
}
export function TicketDueDatesRow({
    ticketDueDate,
    isDueDateHistoryExpanded,
    canExpand,
    metricPathPrefix,
}: {
    ticketDueDate: TicketDueDate;
    isDueDateHistoryExpanded?: boolean;
    canExpand?: boolean;
    metricPathPrefix?: string[];
}): ReactNode | null {
    const dueDate = tryParseDate(ticketDueDate.dueDate) ?? new Date();
    const now = Date.now();

    const completedDate = tryParseDate(ticketDueDate.completedDate);
    const startedDate = tryParseDate(ticketDueDate.startedDate);

    const [isExpanded, setIsExpanded] = useState(false);

    const shouldDisplay = !ticketDueDate.completedDate || isDueDateHistoryExpanded;
    const isInViolation = useIsInViolation({ ticketDueDate, isDueDateHistoryExpanded });

    if (!shouldDisplay) {
        return null;
    }

    let humanizedDate;
    if (completedDate) {
        const start = new Date(ticketDueDate.startedDate ?? now);
        const slaTime = prettyMilliseconds(dueDate.getTime() - start.getTime(), { verbose: true });
        const duration = prettyMilliseconds(completedDate.getTime() - start.getTime(), {
            secondsDecimalDigits: 0,
            compact: true,
        });
        const isLate = dueDate.getTime() < completedDate.getTime();
        humanizedDate = (
            <span title={`Took ${duration}, the SLA was: ${slaTime}`}>
                {isLate ? "Late by" : "Completed in"}
                {" "}
                {duration}
            </span>
        );
    } else if (dueDate.getTime() > now) {
        humanizedDate = (
            <ReactTimeago date={dueDate} title={dueDate.toLocaleString()} />
        );
    } else {
        const due = prettyMilliseconds(now - dueDate.getTime(), {
            secondsDecimalDigits: 0,
            compact: true,
        });
        humanizedDate = (
            <span>
                Overdue by
                {" "}
                {due}
            </span>
        );
    }

    let variant: string;

    let icon = <RiAlarmLine />;
    if (isInViolation && !completedDate) {
        variant = "sla-in-violation";
    } else if (isInViolation && completedDate) {
        variant = "sla-complete-violation";
    } else if (!isInViolation && completedDate) {
        variant = "sla-complete";
    } else {
        variant = "sla-default";
        icon = <RiTimer2Line />;
    }

    const title = (
        <div className={classNames(Styles["sla-header"])}>
            <span className={Styles["sla-header-title"]}>
                {icon}
                {Titles[ticketDueDate.type]}
            </span>
            {humanizedDate}
        </div>
    );

    return !canExpand ? (
        <div className={Styles[variant]}>{title}</div>
    ) : (
        <ExpandingSection
            title={title}
            defaultCardClassName=""
            defaultExpanded={isExpanded}
            className={classNames(Styles.sla)}
            cardClassName={classNames(Styles["sla-card"], Styles[variant])}
            onCollapsed={() => setIsExpanded(false)}
            onExpanded={() => {
                setIsExpanded(true);
                if (metricPathPrefix) {
                    LoggingHandler.instance.metric([...metricPathPrefix, "click"]);
                }
            }}
        >
            {startedDate && (
                <div className={Styles["sla-time-line"]}>
                    <span className={Styles["sla-time-line-title"]}>Triggered:</span>
                    <span>{startedDate.toLocaleString()}</span>
                </div>
            )}
            <div className={Styles["sla-time-line"]}>
                <span className={Styles["sla-time-line-title"]}>Due:</span>
                <span>{dueDate.toLocaleString()}</span>
            </div>
            {completedDate && (
                <div className={Styles["sla-time-line"]}>
                    <span className={Styles["sla-time-line-title"]}>Completed:</span>
                    <span>{completedDate.toLocaleString()}</span>
                </div>
            )}
        </ExpandingSection>

    );
}
TicketDueDatesRow.defaultProps = {
    canExpand: true,
    isDueDateHistoryExpanded: false,
    metricPathPrefix: undefined,
};

export default function Sla({ ticket, metricPathPrefix }: {
    ticket: TicketItem;
    metricPathPrefix?: string[];
}) {
    const incomplete = ticket.ticket?.dueDates?.filter(row => !row.completedDate);
    const latest = incomplete?.min(undefined, row => tryParseDate(row.dueDate)?.getTime() ?? Number.MAX_SAFE_INTEGER);

    useIsInViolation({
        ticketDueDate: latest,
        isDueDateHistoryExpanded: false,
    });

    if (!latest || !ticket.ticket.dueDates) {
        return null;
    }

    let badge: ReactElement | undefined;
    const dueDate = tryParseDate(latest.dueDate) ?? new Date();

    if (dueDate.getTime() < Date.now()) {
        badge = (
            <Badge variant="danger" className={Styles.badge}>
                <RiAlarmLine className={Styles.icon} />
            </Badge>
        );
    } else {
        const totalDuration = (tryParseDate(latest.startedDate)?.getTime() ?? 0) - dueDate.getTime();
        const percentLeft = (Date.now() - dueDate.getTime()) / totalDuration;
        if (percentLeft < 0.1) {
            badge = (
                <Badge variant="yield" className={Styles.badge}>
                    <RiAlarmLine className={Styles.icon} />
                </Badge>
            );
        }
    }

    const popover = (
        <Popover
            arrowProps={{ style: { display: "none" } }}
            className={classNames(
                Styles.popover,
            )}
        >
            <Popover.Body className={classNames(Styles["sla-list-overlay"], "sla-section")}>
                {ticket.ticket.dueDates.map(due => (
                    <TicketDueDatesRow
                        key={due.type + due.dueDate}
                        ticketDueDate={due}
                        canExpand={false}
                        metricPathPrefix={metricPathPrefix}
                    />
                ))}
            </Popover.Body>
        </Popover>
    );

    return badge ? (
        <OverlayTrigger
            trigger={["hover", "focus"]}
            placement="bottom"
            overlay={popover}
            rootClose
        >
            {badge}
        </OverlayTrigger>
    ) : null;
}

Sla.defaultProps = {
    metricPathPrefix: undefined,
};
